export interface IJsonPart {
    start: number;
    end: number;
    json: string;
    parsed: unknown;
}

export interface IFormattedPart {
    type: 'text' | 'json';
    content: string;
    parsed?: unknown;
}

export interface IFormattedOutput {
    parts: IFormattedPart[];
}

/**
 * Find the matching closing bracket for an opening bracket in a string.
 * Handles nested brackets and escaped characters in JSON strings.
 */
export function findMatchingBracket(content: string, startIndex: number, openChar: string, closeChar: string): number {
    let depth = 0;
    let inString = false;
    let escapeNext = false;

    for (let i = startIndex; i < content.length; i++) {
        const char = content[i];

        if (escapeNext) {
            escapeNext = false;
            continue;
        }

        if (char === '\\') {
            escapeNext = true;
            continue;
        }

        if (char === '"' && !escapeNext) {
            inString = !inString;
            continue;
        }

        if (inString) continue;

        if (char === openChar) {
            depth++;
        } else if (char === closeChar) {
            depth--;
            if (depth === 0) {
                return i;
            }
        }
    }

    return -1;
}

/**
 * Extract all valid JSON objects/arrays from a string.
 * Returns non-overlapping matches sorted by start position.
 */
export function extractAllValidJson(content: string): IJsonPart[] {
    if (!content) return [];

    const results: IJsonPart[] = [];
    const jsonStarts: Array<{ index: number; char: string }> = [];

    for (let i = 0; i < content.length; i++) {
        const char = content[i];
        if (char === '{' || char === '[') {
            jsonStarts.push({ index: i, char });
        }
    }

    for (const start of jsonStarts) {
        const openChar = start.char;
        const closeChar = openChar === '{' ? '}' : ']';
        const endIndex = findMatchingBracket(content, start.index, openChar, closeChar);

        if (endIndex > start.index) {
            const potentialJson = content.substring(start.index, endIndex + 1);
            try {
                const parsed = JSON.parse(potentialJson);
                const overlaps = results.some((r) => !(endIndex < r.start || start.index > r.end));
                if (!overlaps) {
                    results.push({ start: start.index, end: endIndex, json: potentialJson, parsed });
                }
            } catch {
                continue;
            }
        }
    }

    results.sort((a, b) => a.start - b.start);
    return results;
}

/**
 * Build formatted output splitting content into text and JSON parts.
 * If entire content is valid JSON, returns single json part.
 * Otherwise extracts embedded JSON objects/arrays and returns mixed parts.
 */
export function buildFormattedOutput(content: string): IFormattedOutput {
    if (!content) return { parts: [] };

    try {
        const parsed = JSON.parse(content);
        return {
            parts: [
                {
                    type: 'json',
                    content: JSON.stringify(parsed, null, 2),
                    parsed,
                },
            ],
        };
    } catch {
        const jsonObjects = extractAllValidJson(content);
        const parts: IFormattedPart[] = [];

        if (jsonObjects.length === 0) {
            return { parts: [{ type: 'text', content }] };
        }

        let lastIndex = 0;

        for (const jsonObj of jsonObjects) {
            if (jsonObj.start > lastIndex) {
                const textBefore = content.substring(lastIndex, jsonObj.start);
                if (textBefore.trim()) {
                    parts.push({ type: 'text', content: textBefore });
                }
            }

            parts.push({
                type: 'json',
                content: JSON.stringify(jsonObj.parsed, null, 2),
                parsed: jsonObj.parsed,
            });

            lastIndex = jsonObj.end + 1;
        }

        if (lastIndex < content.length) {
            const textAfter = content.substring(lastIndex);
            if (textAfter.trim()) {
                parts.push({ type: 'text', content: textAfter });
            }
        }

        return { parts };
    }
}
